import {
  faCheck,
  faChevronDown,
  faChevronUp,
  faSparkles,
} from "@fortawesome/pro-regular-svg-icons";
import { differenceInCalendarDays, formatRelative, parseISO } from "date-fns";
import { upperFirst } from "lodash-es";

import { useUnifiedCopilotSessions } from "src/api/agents/unifiedCopilot";
import {
  FixedPositionedDropdown,
  type FixedPositionedDropdownElement,
} from "src/base-components/FixedPositionedDropDown";
import { Button } from "src/design-system/Button";
import { Icon } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import { useUnifiedCopilotChatSession } from "src/unifiedCopilot/chat/useUnifiedCopilotChatSession";
import { formatDate } from "src/utils/datetime";

const formatSessionLabel = (startedAt: string): string => {
  const date = parseISO(startedAt);
  const now = new Date();
  const daysAgo = differenceInCalendarDays(now, date);

  if (daysAgo === 0 || daysAgo === 1) {
    return upperFirst(formatRelative(date, now));
  }

  return formatDate(date, "MMM dd, yyyy 'at' h:mm aa");
};

const SessionHistoryLoading: React.FC = () => (
  <div className="flex flex-col gap-2 px-4 py-3">
    {Array.from({ length: 4 }, (_, index) => (
      <Skeleton
        key={index}
        dataLoc="unified-copilot-session-history-skeleton"
        height="h-8"
      />
    ))}
  </div>
);

const SessionHistoryMessage: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <div
    className="flex h-24 items-center justify-center px-4 text-center font-inter-normal-12px text-gray-500"
    role="status"
  >
    {children}
  </div>
);

export const UnifiedCopilotSessionHistoryDropdown: React.FC = () => {
  const { sessionId, resumeSession } = useUnifiedCopilotChatSession();
  const {
    sessions,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useUnifiedCopilotSessions();
  const options: FixedPositionedDropdownElement<string>[] = sessions.map(
    (session) => ({
      key: session.session_id,
      value: formatSessionLabel(session.started_at),
    }),
  );

  const handleSessionSelect = (selectedSessionId: string): void => {
    resumeSession(selectedSessionId);
  };

  const handleRetry = (): void => {
    void refetch();
  };

  const handleLoadMore: React.MouseEventHandler<HTMLButtonElement> = (
    event,
  ) => {
    event.stopPropagation();
    void fetchNextPage();
  };

  const renderStatus = (): React.ReactNode => {
    if (isLoading) {
      return <SessionHistoryLoading />;
    }

    if (isError && sessions.length === 0) {
      return (
        <SessionHistoryMessage>
          <div className="flex flex-col items-center gap-2">
            <span>Could not load previous chats.</span>
            <Button
              dataLoc="unified-copilot-session-history-retry"
              size="sm"
              variant="secondary"
              onClick={handleRetry}
            >
              Retry
            </Button>
          </div>
        </SessionHistoryMessage>
      );
    }

    if (sessions.length === 0) {
      return (
        <SessionHistoryMessage>No previous chats yet</SessionHistoryMessage>
      );
    }

    return undefined;
  };

  return (
    <FixedPositionedDropdown<string>
      buttonClassName="h-auto w-auto justify-start rounded-md border-0 bg-transparent px-1 py-0.5 hover:bg-gray-50"
      buttonDataLoc="unified-copilot-session-history-trigger"
      className="h-auto w-fit"
      dataLoc="unified-copilot-session-history"
      elements={options}
      itemsClassNames="w-96 overflow-y-auto [--anchor-max-height:20rem]"
      listMatchesButtonWidth={false}
      placement="bottom start"
      renderButtonValue={(_, isOpen) => (
        <div className="flex cursor-pointer items-center gap-1 text-gray-800">
          <Icon icon={faSparkles} size="xs" />
          <span className="font-inter-normal-13px">Copilot</span>
          <Icon icon={isOpen ? faChevronUp : faChevronDown} size="2xs" />
        </div>
      )}
      renderFooter={
        hasNextPage
          ? () => (
              <div className="flex justify-center py-0.5">
                <button
                  className="px-2 py-0.5 font-inter-medium-12px text-indigo-600 hover:text-indigo-700 focus-visible:underline focus-visible:outline-none disabled:cursor-default disabled:text-gray-400"
                  data-loc="unified-copilot-session-history-load-more"
                  disabled={isFetchingNextPage}
                  type="button"
                  onClick={handleLoadMore}
                >
                  {isFetchingNextPage ? "Loading…" : "Load more"}
                </button>
              </div>
            )
          : undefined
      }
      renderHeader={renderStatus}
      renderValue={({ key, selected, value }) => (
        <div
          aria-current={selected ? "true" : undefined}
          className="flex min-w-0 items-center gap-2"
          data-loc={`unified-copilot-session-${key}`}
        >
          <span className="min-w-0 flex-1 truncate font-inter-normal-13px text-gray-800">
            {value}
          </span>
          {selected && (
            <span className="shrink-0">
              <Icon icon={faCheck} size="xs" variant="active" />
            </span>
          )}
        </div>
      )}
      selected={sessionId ?? undefined}
      onSelect={handleSessionSelect}
    />
  );
};
